import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import {
  MonitoringActiveItemCard,
  MonitoringActiveItemGrid,
  formatMonitoringActiveSince,
} from './MonitoringActiveItemCard';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { apiService } from '../../services/api';
import { LoadingSpinner } from '../LoadingSpinner';
import { AssuntoMonitoringActivationLogList } from '../AssuntoMonitoringActivationLogList';
import { ChangeEmailDeliveryLogList } from '../ChangeEmailDeliveryLogList';
import { FieldInfoPopper } from '../layout/FieldInfoPopper';
import { KEYWORD_SEARCH_SCOPE_INFO, KEYWORD_SEARCH_SCOPE_TITLE } from '../../utils/keywordSearch';

type Props = {
  enabled?: boolean;
};

export function KeywordsMonitoringPanel({ enabled = true }: Props) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);

  const { data, isLoading, refetch } = useQuery(
    ['keywords-monitoring-history'],
    () => apiService.getKeywordsMonitoringHistory(),
    { enabled, refetchOnWindowFocus: false },
  );

  const { data: emailLogs, isLoading: emailLoading } = useQuery(
    ['keywords-monitoring-delivery-logs'],
    () => apiService.getKeywordsMonitoringDeliveryLogs(),
    { enabled, refetchOnWindowFocus: false },
  );

  const toggleMutation = useMutation(
    async ({ keywordKey, activate }: { keywordKey: string; activate: boolean }) => {
      if (activate) {
        return apiService.activateKeywordMonitoringByKey(keywordKey);
      }
      return apiService.deactivateKeywordMonitoring(keywordKey);
    },
    {
      onSuccess: (result) => {
        toast.success(result.message);
        void refetch();
        queryClient.invalidateQueries(['keywords-monitoring']);
        queryClient.invalidateQueries(['keywords-monitoring-history']);
        queryClient.invalidateQueries(['dashboard-changes']);
      },
      onError: () => toast.error('Erro ao alterar monitoramento da palavra-chave'),
    },
  );

  const keywords = data?.keywords ?? [];
  const history = data?.history ?? [];
  const emails = emailLogs ?? [];
  const activeCount = keywords.filter((k) => k.is_monitored).length;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <LoadingSpinner message="Carregando palavras-chave..." />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Palavra-chave
          </Typography>
          <FieldInfoPopper
            title={KEYWORD_SEARCH_SCOPE_TITLE}
            text={KEYWORD_SEARCH_SCOPE_INFO}
            placement="right"
            ariaLabel="Informações sobre monitoramento por palavra-chave"
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {activeCount > 0
            ? `${activeCount} palavra(s)-chave ativa(s) gerando notificações (e-mail D+1 e sininho).`
            : 'Nenhuma palavra-chave ativa. Ative no Dashboard → busca por palavra-chave.'}
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          aria-label="Abas do histórico de palavras-chave"
        >
          <Tab label={`Palavras-chave monitoradas (${activeCount})`} />
          <Tab label={`Logs de ativação (${history.length})`} />
          <Tab label={`E-mails (${emails.length})`} />
        </Tabs>
      </Box>

      {activeTab === 0 &&
        (keywords.length > 0 ? (
          <MonitoringActiveItemGrid>
            {keywords.map((item) => (
              <MonitoringActiveItemCard
                key={item.keyword_key}
                title={item.keyword_label}
                subtitle={
                  item.is_monitored
                    ? formatMonitoringActiveSince(item.last_event?.timestamp)
                    : `Último: ${formatMonitoringActiveSince(item.last_event?.timestamp)}`
                }
                isActive={item.is_monitored}
                onToggle={(checked) =>
                  toggleMutation.mutate({ keywordKey: item.keyword_key, activate: checked })
                }
                toggleLoading={toggleMutation.isLoading}
              />
            ))}
          </MonitoringActiveItemGrid>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">Nenhuma palavra-chave monitorada.</Typography>
          </Box>
        ))}

      {activeTab === 1 && (
        <AssuntoMonitoringActivationLogList
          history={history.map((ev) => ({
            ...ev,
            assunto_label: ev.keyword_label,
            assunto_key: ev.keyword_key,
          }))}
        />
      )}

      {activeTab === 2 && <ChangeEmailDeliveryLogList logs={emails} loading={emailLoading} />}
    </>
  );
}
